import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string | React.ReactNode;
  children?: React.ReactNode;
  error?: boolean;
  semanticId?: string;
}

/** See Checkbox — input opacity must not own ::before/::after (they would disappear). */
const ControlHitArea = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(18)};
  height: ${rem(18)};
  flex-shrink: 0;

  & > input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
  }

  & > input[type='radio'] {
    -webkit-appearance: none;
  }

  &::before {
    position: absolute;
    content: '';
    width: ${rem(18)};
    height: ${rem(18)};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: ${rem(1)} solid var(--ui-border);
    border-radius: 50%;
    background-color: var(--ui-surface);
    transition: 0.3s all ease;
    pointer-events: none;
    z-index: 0;
  }

  &:has(> input:disabled)::before {
    background-color: var(--ui-background-muted);
    border: ${rem(1)} solid var(--ui-border);
  }

  &:has(> input:disabled):has(> input:checked)::before {
    border: ${rem(1)} solid var(--ui-text-subtle);
  }

  &:has(> input:disabled):has(> input:checked)::after {
    background-color: var(--ui-text-subtle);
  }

  &:has(> input:checked)::before {
    border: ${rem(1)} solid var(--ui-status-success);
  }

  &:has(> input:checked)::after {
    position: absolute;
    content: '';
    width: ${rem(10)};
    height: ${rem(10)};
    border-radius: 50%;
    background-color: var(--ui-status-success);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 0;
  }

  &:has(> input.is-invalid:not(:disabled))::before {
    border: ${rem(1)} solid var(--ui-status-danger);
  }
`;

/**
 * Wrapping the entire row in a <label> ensures clicking anywhere — the circle
 * or the text — activates the input without relying on id/for pairing,
 * which can fail when ids contain dots (repeatable field paths).
 * The nested <span> replaces the inner <label> to keep valid HTML.
 */
const Wrapper = styled.label`
  position: relative;
  display: flex;
  gap: ${rem(6)};
  min-width: ${rem(20)};
  align-items: center;
  min-height: ${rem(22)};
  cursor: pointer;

  &[data-disabled='true'] {
    cursor: default;
  }

  span.radio-label {
    margin: 0;
    font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
    font-size: ${rem(16)};
    font-weight: 400;
    transition: 0.3s all linear;
    display: inline-block;
    line-height: ${rem(22)};
    align-self: center;
    color: var(--ui-text);
  }
`;

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, children, error = false, semanticId, id, name, disabled, className, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const inputId = id ?? name;
  const displayLabel = label ?? children;

  return (
    <Wrapper className={className} data-disabled={disabled ? 'true' : undefined} {...semanticProps}>
      <ControlHitArea>
        <input
          ref={ref}
          type="radio"
          id={inputId}
          name={name}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={error ? 'is-invalid' : ''}
          {...rest}
        />
      </ControlHitArea>
      {displayLabel != null && (
        <span className="radio-label">
          {displayLabel}
        </span>
      )}
    </Wrapper>
  );
});

Radio.displayName = 'Radio';
